import { FC, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type {
  Cut,
  CutsheetCatalog,
  Extra,
  FreeSelectionQuestion,
  Trim,
} from '../../../types';
import { DropZone } from './drop-zone';
import { SortableItem } from './sortable-item';

export interface FreeSelectionContentProps {
  question: FreeSelectionQuestion;
  catalog: CutsheetCatalog;
  canAccept: boolean | null;
  draggingType: string | null;
  onAddItem?: (questionId: string, itemId: string) => void;
  onRemoveItem?: (questionId: string, itemId: string) => void;
  onEditItem?: (itemId: string, itemType: 'cut' | 'trim' | 'extra') => void;
  onReorderItems?: (questionId: string, newOrderIds: string[]) => void;
  onAddClick: () => void;
}

/**
 * Content panel for a FreeSelection question.
 *
 * Renders a vertically-sortable list of selected catalog items (via dnd-kit)
 * and a droppable zone at the bottom for adding new items.
 * @param root0
 * @param root0.question
 * @param root0.catalog
 * @param root0.canAccept
 * @param root0.draggingType
 * @param root0.onAddItem
 * @param root0.onRemoveItem
 * @param root0.onEditItem
 * @param root0.onReorderItems
 * @param root0.onAddClick
 */
export const FreeSelectionContent: FC<FreeSelectionContentProps> = ({
  question,
  catalog,
  canAccept,
  draggingType,
  onAddItem,
  onRemoveItem,
  onEditItem,
  onReorderItems,
  onAddClick,
}) => {
  const allItems = useMemo(
    () => [...catalog.cuts, ...catalog.trim, ...catalog.extras],
    [catalog],
  );

  const freeSelectionItems = useMemo(
    () =>
      question.availableCutIds
        .map((id) => allItems.find((item) => item.id === id))
        .filter(Boolean) as (Cut | Trim | Extra)[],
    [question.availableCutIds, allItems],
  );

  /**
   * Looks up which catalog collection an item belongs to
   * @param itemId
   */
  const getItemType = (itemId: string): 'cut' | 'trim' | 'extra' | null => {
    if (catalog.cuts.find((c) => c.id === itemId)) return 'cut';
    if (catalog.trim.find((t) => t.id === itemId)) return 'trim';
    if (catalog.extras.find((e) => e.id === itemId)) return 'extra';
    return null;
  };

  // Sensors for the inner DndContext that handles item reordering
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleReorder = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorderItems) return;
    const oldIndex = question.availableCutIds.indexOf(active.id as string);
    const newIndex = question.availableCutIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorderItems(
      question.id,
      arrayMove(question.availableCutIds, oldIndex, newIndex),
    );
  };

  return (
    <Stack spacing={1}>
      {freeSelectionItems.length > 0 && (
        <Box>
          {/* Inner DndContext scoped to item reordering only (separate from outer page DnD) */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleReorder}
          >
            <SortableContext
              items={freeSelectionItems.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={0.5}>
                {freeSelectionItems.map((item) => {
                  const itemType = getItemType(item.id);
                  return (
                    <SortableItem
                      key={item.id}
                      item={item}
                      itemType={itemType}
                      onEdit={
                        onEditItem && itemType
                          ? () => onEditItem(item.id, itemType)
                          : undefined
                      }
                      onRemove={
                        onRemoveItem
                          ? () => onRemoveItem(question.id, item.id)
                          : undefined
                      }
                    />
                  );
                })}
              </Stack>
            </SortableContext>
          </DndContext>
        </Box>
      )}

      {/* Dropzone for adding items via drag or click */}
      {onAddItem && (
        <DropZone
          droppableId={`question-dropzone-${question.id}`}
          droppableData={{ type: 'question-dropzone', questionId: question.id }}
          canAccept={canAccept}
          draggingType={draggingType}
          incompatibleMessage="This question only accepts cuts"
          placeholder="Drop or click to add items"
          onClick={onAddClick}
        />
      )}
    </Stack>
  );
};
